"use client";
import { useState, useEffect, useRef } from "react";
import { detectMeexExtension } from "./extensionDetection";

export function useExtensionStatus() {
  const [extensionState, setExtensionState] = useState({
    installed: null, // null = loading, true = installed, false = not installed
    version: null,
    checkingComplete: false,
  });
  const retryTimerRef = useRef(null);

  // preliminary=true: if not found, keep checkingComplete=false so the UI
  // stays in "still checking" state while a retry is scheduled. This prevents
  // a false-negative flash of "Install Extension" caused by the race between
  // component mount and the extension's content script injecting its markers.
  const checkExtension = async ({ preliminary = false } = {}) => {
    if (typeof window !== "undefined") {
      try {
        const result = await detectMeexExtension();
        setExtensionState({
          installed: result.installed,
          version: result.version,
          // Mark complete immediately when installed, or when this is the
          // definitive (non-preliminary) check.
          checkingComplete: result.installed || !preliminary,
        });
        return result;
      } catch (error) {
        console.error("Failed to detect extension:", error);
        setExtensionState({
          installed: false,
          version: null,
          checkingComplete: !preliminary,
        });
        return { installed: false, version: null };
      }
    }
  };

  // Check on mount. If the first pass misses the extension (content script
  // not yet injected), retry once after 1.5s before marking as not installed.
  useEffect(() => {
    checkExtension({ preliminary: true }).then((result) => {
      if (!result?.installed) {
        retryTimerRef.current = setTimeout(() => {
          checkExtension({ preliminary: false });
        }, 1500);
      }
    });

    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { ...extensionState, recheckExtension: checkExtension };
}
